<p-toast></p-toast>

<p-sidebar
  [(visible)]="visible"
  position="right"
  [style]="{ width: '540px', maxWidth: '100vw' }"
  styleClass="opportunity-drawer"
  [blockScroll]="true"
  [modal]="false"
  (onHide)="onHide()">

  <ng-template pTemplate="header">
    <div class="drawer-header" *ngIf="opportunity">
      <div class="drawer-header-copy">
        <h3>{{ opportunity.title }}</h3>
        <p *ngIf="opportunity.description">{{ opportunity.description }}</p>
      </div>
      <span [ngClass]="getTypeBadgeClass(opportunity.type)">{{ getTypeLabel(opportunity.type) }}</span>
    </div>
  </ng-template>

  <ng-container *ngIf="opportunity">
    <div class="drawer-content">
      <section class="panel-section metrics-overview">
        <div class="metrics-grid">
          <article class="metric-card metric-card-wide">
            <div class="metric-head">
              <span>Signal Strength</span>
              <strong>{{ getSignalStrength() }}%</strong>
            </div>
            <p-progressBar [value]="getSignalStrength()" [showValue]="false" styleClass="signal-progress"></p-progressBar>
          </article>

          <article class="metric-card">
            <span class="metric-label">Estimated Impact</span>
            <strong>~{{ opportunity.estimatedImpact?.clicks | number }} clicks</strong>
            <small>{{ opportunity.estimatedImpact?.impressions | number }} impressions / month</small>
          </article>

          <article class="metric-card">
            <span class="metric-label">Effort</span>
            <span [ngClass]="getEffortBadgeClass(opportunity.effort)">{{ opportunity.effort }}</span>
          </article>

          <article class="metric-card">
            <span class="metric-label">Business Impact</span>
            <span *ngIf="opportunity.businessImpactScore" [ngClass]="getImpactBadgeClass(opportunity.businessImpactScore)">
              {{ opportunity.businessImpactScore }}
            </span>
            <strong *ngIf="!opportunity.businessImpactScore">—</strong>
          </article>
        </div>
      </section>

      <section class="panel-section" *ngIf="opportunity.relatedCampaigns?.length">
        <div class="section-title">Related Campaigns</div>
        <ul class="detail-list">
          <li *ngFor="let campaign of opportunity.relatedCampaigns">
            <i class="pi pi-megaphone"></i>
            <span>{{ campaign }}</span>
          </li>
        </ul>
      </section>

      <!-- GA4 Context -->
      <section class="panel-section" *ngIf="hasGaContext()">
        <div class="section-title">GA4 Context</div>

        <div class="ga-callout" [ngClass]="isGaConnected() ? 'ga-ok' : 'ga-warning'">
          <i class="pi" [ngClass]="isGaConnected() ? 'pi-check-circle' : 'pi-exclamation-triangle'"></i>
          <span>{{ isGaConnected() ? 'GA4 connected' : (opportunity.gaContext?.gaErrorMessage || 'GA4 is not connected for this project.') }}</span>
        </div>

        <div class="ga-grid">
          <div>
            <span>Engagement Rate</span>
            <strong>{{ formatPercent(opportunity.gaContext?.engagementRate) }}</strong>
          </div>
          <div>
            <span>Prior Engagement</span>
            <strong>{{ formatPercent(opportunity.gaContext?.engagementRatePrior) }}</strong>
          </div>
          <div>
            <span>Avg Session Duration</span>
            <strong>{{ formatDurationSeconds(opportunity.gaContext?.avgSessionDuration) }}</strong>
          </div>
          <div>
            <span>Event Count</span>
            <strong>{{ opportunity.gaContext?.eventCount ?? '—' }}</strong>
          </div>
        </div>
      </section>

      <!-- AI Summary -->
      <section class="panel-section">
        <div class="section-head-row">
          <span class="section-title">AI Summary</span>
          <button
            pButton
            type="button"
            icon="pi pi-refresh"
            class="p-button-rounded p-button-text p-button-sm"
            [disabled]="isRegeneratingLocal"
            (click)="regenerateSummary()"
            pTooltip="Regenerate summary"
            tooltipPosition="left">
          </button>
        </div>

        <div *ngIf="isRegeneratingLocal" class="state-card state-pending">
          <i class="pi pi-spin pi-spinner"></i>
          <span>Generating summary...</span>
        </div>

        <div *ngIf="!isRegeneratingLocal && isSummaryFailed()" class="state-card state-error">
          <div class="state-title">Summary generation failed</div>
          <div *ngIf="opportunity.aiSummaryError">{{ opportunity.aiSummaryError }}</div>
        </div>

        <div *ngIf="opportunity.aiSummary && !isRegeneratingLocal && !isSummaryFailed()" class="state-card state-success">
          <div class="state-title" *ngIf="opportunity.aiSummary.headline">{{ opportunity.aiSummary.headline }}</div>
          <div *ngIf="opportunity.aiSummary.executiveSummary">{{ opportunity.aiSummary.executiveSummary }}</div>
          <div *ngIf="opportunity.aiSummary.whyItMatters">{{ opportunity.aiSummary.whyItMatters }}</div>
          <ul class="detail-list" *ngIf="opportunity.aiSummary.recommendedActions?.length">
            <li *ngFor="let action of opportunity.aiSummary.recommendedActions">{{ action }}</li>
          </ul>
          <div class="state-meta" *ngIf="opportunity.aiSummaryGeneratedAt">
            <i class="pi pi-clock"></i>
            <span>Generated {{ opportunity.aiSummaryGeneratedAt | date:'short' }}</span>
            <span *ngIf="opportunity.aiSummaryTokenCost">({{ opportunity.aiSummaryTokenCost | number:'1.0-0' }} tokens)</span>
          </div>
        </div>

        <div *ngIf="!opportunity.aiSummary && !isRegeneratingLocal && !isSummaryFailed()" class="state-empty">
          No summary generated yet.
        </div>
      </section>

      <section class="panel-section" *ngIf="opportunity.evidence?.length > 0">
        <div class="section-title">Evidence</div>
        <ul class="detail-list detail-list-links">
          <li *ngFor="let ev of opportunity.evidence">
            <span class="evidence-source-chip">{{ getEvidenceSourceLabel(ev.source) }}</span>
            <ng-container *ngIf="ev.url; else plainSummary">
              <a [href]="ev.url" target="_blank" rel="noopener noreferrer">{{ ev.summary }}</a>
            </ng-container>
            <ng-template #plainSummary>{{ ev.summary }}</ng-template>
          </li>
        </ul>
      </section>

      <section class="panel-section" *ngIf="opportunity.actionSteps?.length > 0">
        <div class="section-title">Action Steps</div>
        <ol class="steps-list">
          <li *ngFor="let step of opportunity.actionSteps">{{ step }}</li>
        </ol>
      </section>

      <!-- Status / Assignment Form -->
      <section class="panel-section">
        <div class="section-title">Status &amp; Assignment</div>

        <div class="field mb-2">
          <label>Status</label>
          <p-dropdown
            *ngIf="canEdit; else statusReadonly"
            [options]="statusOptions"
            [(ngModel)]="editStatus"
            optionLabel="label"
            optionValue="value"
            styleClass="w-full mt-1">
          </p-dropdown>
          <ng-template #statusReadonly>
            <div class="readonly-value mt-1">{{ getStatusLabel(editStatus) }}</div>
          </ng-template>
        </div>

        <div class="field mb-2">
          <label>Assigned To</label>
          <input
            *ngIf="canEdit; else assigneeReadonly"
            pInputText
            [(ngModel)]="editAssignedTo"
            class="w-full mt-1"
            placeholder="Enter email or name" />
          <ng-template #assigneeReadonly>
            <div class="readonly-value mt-1">{{ editAssignedTo || '—' }}</div>
          </ng-template>
        </div>

        <div class="field mb-3">
          <label>Due Date</label>
          <p-calendar
            *ngIf="canEdit; else dueDateReadonly"
            [(ngModel)]="editDueDate"
            styleClass="w-full mt-1"
            dateFormat="yy-mm-dd"
            [showIcon]="true">
          </p-calendar>
          <ng-template #dueDateReadonly>
            <div class="readonly-value mt-1">{{ editDueDate ? (editDueDate | date:'mediumDate') : '—' }}</div>
          </ng-template>
        </div>

        <button
          *ngIf="canEdit"
          pButton
          label="Save Changes"
          icon="pi pi-check"
          class="p-button-sm w-full"
          (click)="onSave()">
        </button>
      </section>

      <section class="panel-section" *ngIf="opportunity.status === 'DONE' && opportunity.outcome">
        <div class="section-title">Outcome</div>
        <div class="outcome-row">
          <span>Before</span>
          <strong>{{ opportunity.outcome.clicksBefore | number }} clicks</strong>
        </div>
        <div class="outcome-row">
          <span>After</span>
          <strong>{{ opportunity.outcome.clicksAfter | number }} clicks</strong>
        </div>
        <div class="outcome-row" *ngIf="opportunity.outcome.measuredAt">
          <span>Measured</span>
          <strong>{{ opportunity.outcome.measuredAt | date:'mediumDate' }}</strong>
        </div>
      </section>
    </div>
  </ng-container>

</p-sidebar>
